import * as THREE from 'three';
import { getAssetPath } from '../utils/assetPath';
import { PlayerConfig } from '../config/PlayerConfig';
import techVert from '../shaders/tech/tech.vert';
import techFrag from '../shaders/tech/tech.frag';
import { ItemType } from '../player/Inventory';

export interface PrintJob {
  itemType: ItemType;
  progress: number;  // 0 to 1
  totalTime: number; // in seconds
  startTime: number; // timestamp
  outputQuantity: number; // how many items this job produces
  ingredients: Map<ItemType, number>; // ingredients used for refund on cancel
}

// Queued item waiting to be printed
export interface QueuedPrintJob {
  itemType: ItemType;
  totalTime: number;
  outputQuantity: number;
  // Store the ingredients used so we can refund on cancel
  ingredients: Map<ItemType, number>;
}

// Output inventory slot
export interface PrinterOutputSlot {
  itemType: ItemType | null;
  quantity: number;
}

export interface PlacedPrinter3D {
  mesh: THREE.Mesh;
  position: THREE.Vector3;
  tileIndex: number;
  rotation: number;
  isPowered: boolean;
  currentJob: PrintJob | null;
  // Print queue (items waiting to be printed)
  printQueue: QueuedPrintJob[];
  // Output inventory (2x3 = 6 slots)
  outputInventory: PrinterOutputSlot[];
}

export class Printer3DManager {
  private scene: THREE.Scene;
  private printers: PlacedPrinter3D[] = [];
  private printerMeshes: THREE.Mesh[] = [];
  private textureLoader: THREE.TextureLoader;
  private printerGeometry: THREE.BoxGeometry | null = null;
  private printerMaterial: THREE.ShaderMaterial | null = null;

  // Planet lighting uniforms
  private planetCenter: THREE.Vector3;
  private sunDirection: THREE.Vector3;

  // Printer size (2x the size of other tech blocks)
  private readonly PRINTER_SIZE = 1.6;

  constructor(scene: THREE.Scene, planetCenter?: THREE.Vector3, sunDirection?: THREE.Vector3) {
    this.scene = scene;
    this.textureLoader = new THREE.TextureLoader();
    this.planetCenter = planetCenter?.clone() || new THREE.Vector3(0, 0, 0);
    this.sunDirection = sunDirection?.clone() || new THREE.Vector3(1, 0, 0);
    this.initGeometryAndMaterials();
  }

  public setSunDirection(direction: THREE.Vector3): void {
    this.sunDirection.copy(direction);
    if (this.printerMaterial) {
      this.printerMaterial.uniforms.sunDirection.value.copy(direction);
    }
  }

  public setPlanetCenter(center: THREE.Vector3): void {
    this.planetCenter.copy(center);
    if (this.printerMaterial) {
      this.printerMaterial.uniforms.planetCenter.value.copy(center);
    }
  }

  private async initGeometryAndMaterials(): Promise<void> {
    const texture = await new Promise<THREE.Texture>((resolve, reject) => {
      this.textureLoader.load(
        getAssetPath('/textures/technology/3d_printer.png'),
        resolve,
        undefined,
        reject
      );
    });

    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    this.printerGeometry = new THREE.BoxGeometry(
      this.PRINTER_SIZE,
      this.PRINTER_SIZE,
      this.PRINTER_SIZE
    );

    const faceUVs = this.calculateFaceUVs();
    const uvAttribute = this.printerGeometry.attributes.uv;
    const uvArray = uvAttribute.array as Float32Array;

    const applyFaceUV = (faceIndex: number, uv: { u1: number; v1: number; u2: number; v2: number }, flipHorizontal = false, flipVertical = false) => {
      const baseIndex = faceIndex * 8;
      const left = flipHorizontal ? uv.u2 : uv.u1;
      const right = flipHorizontal ? uv.u1 : uv.u2;
      const bottom = flipVertical ? uv.v2 : uv.v1;
      const top = flipVertical ? uv.v1 : uv.v2;

      uvArray[baseIndex + 0] = left;  uvArray[baseIndex + 1] = bottom;
      uvArray[baseIndex + 2] = right; uvArray[baseIndex + 3] = bottom;
      uvArray[baseIndex + 4] = left;  uvArray[baseIndex + 5] = top;
      uvArray[baseIndex + 6] = right; uvArray[baseIndex + 7] = top;
    };

    applyFaceUV(0, faceUVs.right, true, true);  // +X (right side)
    applyFaceUV(1, faceUVs.left, false, true);  // -X (left side)
    applyFaceUV(2, faceUVs.top, false, false);  // +Y (top)
    applyFaceUV(3, faceUVs.bottom, false, false); // -Y (bottom)
    applyFaceUV(4, faceUVs.front, false, true); // +Z (front)
    applyFaceUV(5, faceUVs.back, true, true);   // -Z (back)

    uvAttribute.needsUpdate = true;

    this.printerMaterial = new THREE.ShaderMaterial({
      uniforms: {
        techTexture: { value: texture },
        sunDirection: { value: this.sunDirection.clone() },
        planetCenter: { value: this.planetCenter.clone() },
        ambientIntensity: { value: PlayerConfig.AMBIENT_LIGHT_INTENSITY },
        directionalIntensity: { value: PlayerConfig.DIRECTIONAL_LIGHT_INTENSITY },
        torchLight: { value: 0.0 },
      },
      vertexShader: techVert,
      fragmentShader: techFrag,
    });
  }

  private calculateFaceUVs(): {
    front: { u1: number; v1: number; u2: number; v2: number };
    left: { u1: number; v1: number; u2: number; v2: number };
    right: { u1: number; v1: number; u2: number; v2: number };
    back: { u1: number; v1: number; u2: number; v2: number };
    top: { u1: number; v1: number; u2: number; v2: number };
    bottom: { u1: number; v1: number; u2: number; v2: number };
  } {
    // Texture atlas layout (48x32, 3x2 grid of 16x16 cells):
    // Row 1: Front, Left, Right
    // Row 2: Back, Top, Bottom
    const texWidth = 48;
    const texHeight = 32;
    const cellSize = 16;

    const uv = (x: number, y: number, w: number, h: number) => ({
      u1: x / texWidth,
      v1: 1 - (y + h) / texHeight,
      u2: (x + w) / texWidth,
      v2: 1 - y / texHeight,
    });

    return {
      front: uv(0, 0, cellSize, cellSize),              // Row 1, Col 0
      left: uv(cellSize, 0, cellSize, cellSize),        // Row 1, Col 1
      right: uv(cellSize * 2, 0, cellSize, cellSize),   // Row 1, Col 2
      back: uv(0, cellSize, cellSize, cellSize),        // Row 2, Col 0
      top: uv(cellSize, cellSize, cellSize, cellSize),  // Row 2, Col 1
      bottom: uv(cellSize * 2, cellSize, cellSize, cellSize), // Row 2, Col 2
    };
  }

  public async placePrinter3D(
    worldPosition: THREE.Vector3,
    planetCenter: THREE.Vector3,
    tileIndex: number,
    playerForward?: THREE.Vector3
  ): Promise<PlacedPrinter3D | null> {
    if (!this.printerGeometry || !this.printerMaterial) {
      await this.initGeometryAndMaterials();
    }

    if (!this.printerGeometry || !this.printerMaterial) {
      console.error('Failed to initialize 3D printer geometry/materials');
      return null;
    }

    const instanceMaterial = this.printerMaterial.clone();
    const mesh = new THREE.Mesh(this.printerGeometry, instanceMaterial);

    const upDirection = worldPosition.clone().sub(planetCenter).normalize();
    const printerPosition = worldPosition.clone().add(upDirection.multiplyScalar(this.PRINTER_SIZE / 2));
    mesh.position.copy(printerPosition);

    const up = printerPosition.clone().sub(planetCenter).normalize();

    let tangent = new THREE.Vector3(1, 0, 0);
    if (Math.abs(up.dot(tangent)) > 0.9) {
      tangent = new THREE.Vector3(0, 0, 1);
    }
    const bitangent = new THREE.Vector3().crossVectors(up, tangent).normalize();
    tangent.crossVectors(bitangent, up).normalize();

    let facingAngle = 0;
    if (playerForward) {
      const projectedForward = playerForward.clone();
      projectedForward.sub(up.clone().multiplyScalar(projectedForward.dot(up)));
      projectedForward.normalize();

      const towardPlayer = projectedForward.clone().negate();
      const angleFromTangent = Math.atan2(towardPlayer.dot(bitangent), towardPlayer.dot(tangent));

      if (PlayerConfig.TECH_BLOCK_HEX_SNAP) {
        const snapAngle = Math.PI / 3;
        facingAngle = Math.round(angleFromTangent / snapAngle) * snapAngle;
      } else {
        facingAngle = angleFromTangent;
      }
    }

    const offsetAngle = PlayerConfig.TECH_BLOCK_ROTATION_OFFSET * (Math.PI / 180);
    facingAngle += offsetAngle;

    const alignQuaternion = new THREE.Quaternion();
    alignQuaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);

    const rotateY = new THREE.Quaternion();
    rotateY.setFromAxisAngle(up, facingAngle);

    mesh.quaternion.copy(rotateY).multiply(alignQuaternion);

    mesh.userData.is3DPrinter = true;
    mesh.userData.tileIndex = tileIndex;

    this.scene.add(mesh);

    const printer: PlacedPrinter3D = {
      mesh,
      position: printerPosition.clone(),
      tileIndex,
      rotation: facingAngle,
      isPowered: false,
      currentJob: null,
      printQueue: [],
      outputInventory: this.createEmptyOutputInventory(),
    };

    this.printers.push(printer);
    this.printerMeshes.push(mesh);

    return printer;
  }

  // Create empty 2x3 output inventory
  private createEmptyOutputInventory(): PrinterOutputSlot[] {
    const slots: PrinterOutputSlot[] = [];
    for (let i = 0; i < 6; i++) {
      slots.push({ itemType: null, quantity: 0 });
    }
    return slots;
  }

  public async restorePrinter3D(
    savedPosition: THREE.Vector3,
    planetCenter: THREE.Vector3,
    tileIndex: number,
    rotation: number,
    isPowered: boolean = false,
    currentJob: PrintJob | null = null,
    printQueue: QueuedPrintJob[] = [],
    outputInventory: PrinterOutputSlot[] | null = null
  ): Promise<PlacedPrinter3D | null> {
    if (!this.printerGeometry || !this.printerMaterial) {
      await this.initGeometryAndMaterials();
    }

    if (!this.printerGeometry || !this.printerMaterial) {
      console.error('Failed to initialize 3D printer geometry/materials');
      return null;
    }

    const instanceMaterial = this.printerMaterial.clone();
    const mesh = new THREE.Mesh(this.printerGeometry, instanceMaterial);

    mesh.position.copy(savedPosition);

    const up = savedPosition.clone().sub(planetCenter).normalize();
    const alignQuaternion = new THREE.Quaternion();
    alignQuaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);

    const rotateY = new THREE.Quaternion();
    rotateY.setFromAxisAngle(up, rotation);

    mesh.quaternion.copy(rotateY).multiply(alignQuaternion);

    mesh.userData.is3DPrinter = true;
    mesh.userData.tileIndex = tileIndex;

    this.scene.add(mesh);

    const printer: PlacedPrinter3D = {
      mesh,
      position: savedPosition.clone(),
      tileIndex,
      rotation,
      isPowered,
      currentJob,
      printQueue: printQueue,
      outputInventory: outputInventory || this.createEmptyOutputInventory(),
    };

    this.printers.push(printer);
    this.printerMeshes.push(mesh);

    return printer;
  }

  public removePrinter3D(printer: PlacedPrinter3D): void {
    const index = this.printers.indexOf(printer);
    if (index !== -1) {
      this.scene.remove(printer.mesh);
      if (printer.mesh.geometry) printer.mesh.geometry.dispose();
      if (printer.mesh.material) {
        if (Array.isArray(printer.mesh.material)) {
          printer.mesh.material.forEach(m => m.dispose());
        } else {
          printer.mesh.material.dispose();
        }
      }
      this.printers.splice(index, 1);
      const meshIndex = this.printerMeshes.indexOf(printer.mesh);
      if (meshIndex !== -1) {
        this.printerMeshes.splice(meshIndex, 1);
      }
    }
  }

  public getPrinter3DAtTile(tileIndex: number): PlacedPrinter3D | undefined {
    return this.printers.find(p => p.tileIndex === tileIndex);
  }

  public getPrinter3DByMesh(mesh: THREE.Mesh): PlacedPrinter3D | undefined {
    return this.printers.find(p => p.mesh === mesh);
  }

  public getPrinter3DMeshes(): THREE.Mesh[] {
    return this.printerMeshes;
  }

  public getAllPrinters(): PlacedPrinter3D[] {
    return this.printers;
  }

  public setPrinterPowered(printer: PlacedPrinter3D, isPowered: boolean): void {
    printer.isPowered = isPowered;
  }

  // Add a job to the print queue
  public queuePrintJob(
    printer: PlacedPrinter3D,
    itemType: ItemType,
    totalTime: number,
    outputQuantity: number,
    ingredients: Map<ItemType, number>
  ): boolean {
    printer.printQueue.push({
      itemType,
      totalTime,
      outputQuantity,
      ingredients,
    });
    return true;
  }

  // Cancel a queued job and return its ingredients
  public cancelQueuedJob(printer: PlacedPrinter3D, queueIndex: number): QueuedPrintJob | null {
    if (queueIndex < 0 || queueIndex >= printer.printQueue.length) {
      return null;
    }
    const job = printer.printQueue.splice(queueIndex, 1)[0];
    return job;
  }

  // Try to add completed item to output inventory
  private addToOutputInventory(printer: PlacedPrinter3D, itemType: ItemType, quantity: number): boolean {
    // First try to stack with existing items of same type
    for (const slot of printer.outputInventory) {
      if (slot.itemType === itemType && slot.quantity < 64) {
        const canAdd = Math.min(quantity, 64 - slot.quantity);
        slot.quantity += canAdd;
        quantity -= canAdd;
        if (quantity <= 0) return true;
      }
    }

    // Then try empty slots
    for (const slot of printer.outputInventory) {
      if (slot.itemType === null) {
        const canAdd = Math.min(quantity, 64);
        slot.itemType = itemType;
        slot.quantity = canAdd;
        quantity -= canAdd;
        if (quantity <= 0) return true;
      }
    }

    // Output inventory is full
    return quantity <= 0;
  }

  // Check if output inventory has space for an item
  public hasOutputSpace(printer: PlacedPrinter3D, itemType: ItemType, quantity: number): boolean {
    let remaining = quantity;

    // Check existing stacks
    for (const slot of printer.outputInventory) {
      if (slot.itemType === itemType && slot.quantity < 64) {
        remaining -= (64 - slot.quantity);
        if (remaining <= 0) return true;
      }
    }

    // Check empty slots
    for (const slot of printer.outputInventory) {
      if (slot.itemType === null) {
        remaining -= 64;
        if (remaining <= 0) return true;
      }
    }

    return remaining <= 0;
  }

  // Take item from output inventory
  public takeFromOutput(printer: PlacedPrinter3D, slotIndex: number, quantity: number): { itemType: ItemType; quantity: number } | null {
    if (slotIndex < 0 || slotIndex >= printer.outputInventory.length) {
      return null;
    }

    const slot = printer.outputInventory[slotIndex];
    if (slot.itemType === null || slot.quantity <= 0) {
      return null;
    }

    const takeAmount = Math.min(quantity, slot.quantity);
    const result = { itemType: slot.itemType, quantity: takeAmount };

    slot.quantity -= takeAmount;
    if (slot.quantity <= 0) {
      slot.itemType = null;
      slot.quantity = 0;
    }

    return result;
  }

  public updatePrintJobs(deltaTime: number): void {
    for (const printer of this.printers) {
      // If no current job but queue has items, start next job
      if (!printer.currentJob && printer.printQueue.length > 0) {
        const nextJob = printer.printQueue[0];
        // Check if output has space before starting
        if (this.hasOutputSpace(printer, nextJob.itemType, nextJob.outputQuantity)) {
          printer.printQueue.shift(); // Remove from queue
          printer.currentJob = {
            itemType: nextJob.itemType,
            progress: 0,
            totalTime: nextJob.totalTime,
            startTime: Date.now(),
            outputQuantity: nextJob.outputQuantity,
            ingredients: nextJob.ingredients,
          };
        }
      }

      // Process current job (no power requirement anymore)
      if (printer.currentJob) {
        printer.currentJob.progress += deltaTime / printer.currentJob.totalTime;
        if (printer.currentJob.progress >= 1) {
          printer.currentJob.progress = 1;
          // Job completed - add to output inventory
          this.addToOutputInventory(
            printer,
            printer.currentJob.itemType,
            printer.currentJob.outputQuantity
          );
          printer.currentJob = null;
        }
      }
    }
  }

  public cancelPrintJob(printer: PlacedPrinter3D): PrintJob | null {
    const job = printer.currentJob;
    printer.currentJob = null;
    return job;
  }

  public collectPrintJob(printer: PlacedPrinter3D): ItemType | null {
    if (printer.currentJob && printer.currentJob.progress >= 1) {
      const itemType = printer.currentJob.itemType;
      printer.currentJob = null;
      return itemType;
    }
    return null;
  }

  // Update torch lighting for all printers based on nearby torch positions
  public updateTorchLighting(torchPositions: THREE.Vector3[], torchRange: number, torchIntensity: number): void {
    for (const printer of this.printers) {
      let totalLight = 0;

      for (const torchPos of torchPositions) {
        const distance = printer.position.distanceTo(torchPos);
        if (distance < torchRange) {
          // Inverse square falloff with decay (matching terrain)
          const decay = 2; // Same as TorchConfig.LIGHT_DECAY
          const attenuation = 1 / (1 + decay * distance * distance / (torchRange * torchRange));
          totalLight += attenuation * torchIntensity;
        }
      }

      // Clamp and apply to material uniform
      totalLight = Math.min(1.5, totalLight);
      const material = printer.mesh.material as THREE.ShaderMaterial;
      if (material.uniforms && material.uniforms.torchLight) {
        material.uniforms.torchLight.value = totalLight;
      }
    }
  }

  public dispose(): void {
    for (const printer of this.printers) {
      this.scene.remove(printer.mesh);
      if (printer.mesh.geometry) printer.mesh.geometry.dispose();
      if (printer.mesh.material) {
        if (Array.isArray(printer.mesh.material)) {
          printer.mesh.material.forEach(m => m.dispose());
        } else {
          printer.mesh.material.dispose();
        }
      }
    }
    this.printers = [];
    this.printerMeshes = [];

    if (this.printerGeometry) {
      this.printerGeometry.dispose();
      this.printerGeometry = null;
    }
    if (this.printerMaterial) {
      this.printerMaterial.dispose();
      this.printerMaterial = null;
    }
  }
}
